import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Subject } from "rxjs";
import { filter, switchMap, takeUntil } from "rxjs/operators";

import { CoachMarkService } from "../coach-mark.service";
import { InteractionTrackingService, InteractionType } from "../interaction-tracking.service";
import { OnboardingStep, OnboardingStepService } from "../onboarding-step.service";
import { PaymentsService } from "../payments.service";
import { ScoreContextualEntryService } from "./score-contextual-entry.service";
import { SS01_LESSON_ID, SS01SuperStarsService } from "./ss01-super-stars.service";

export const SS02_LESSON_ID = "SS02";

/** Recorded with SS02's completed lesson state - its CompletedAt *is* this milestone. */
export const SCORE_SUPER_STARS_QUICK_ADD_INTRODUCED = "score_super_stars_quick_add_introduced";

/** Anchors workbook v2.38: the + control beside a Super Star inside the Super Stars modal. */
export const SS02_ANCHOR = "score.super_star_add";

/**
 * Copy rows SS02.01-SS02.02, workbook v2.38 Copy sheet (verbatim). SS02.01 is still flagged
 * "Needs confirmation" in the Copy sheet - expect a swap.
 */
export const SS02_COPY = {
  heading: "Add a Super Star",
  body:
    "Select + beside a Super Star to add it to your current Score. Once added, score its strength " +
    "and add the details that fit right now.",
};

/**
 * Onboarding lesson SS02 "Super Stars quick-add" (FLIP-372; spec v2.36 §18.4, workbook v2.38).
 * Navigation step, shown as an anchored prompt (no buttons) over the Super Stars modal, at the +
 * beside the first Super Star. DialogSuperStarsComponent drives it: onDialogOpened on open,
 * onStarSelected when a Star's + is used, onDialogClosed when it closes.
 *
 * - Eligible while the `onboarding-walkthrough` flag is on, SS01 (FLIP-371) is resolved and SS02
 *   is not, and SS01B has resolved or was never eligible (SS01 resolved by opening Super Stars
 *   directly, or on an earlier visit), the first time the Super Stars modal opens - from the Score
 *   landing page or from inside an active Scoring session (only if the user opens it). Any failed
 *   check holds back.
 * - Selecting + or the visible close completes SS02 - the close is the task dismissal overridden to
 *   complete rather than defer. Selecting + is never required.
 * - Closing the modal while the prompt shows just hides it and records nothing, so it can show the
 *   next time the modal opens.
 */
@Injectable({ providedIn: "root" })
export class SS02SuperStarsQuickAddService {
  private readonly close$ = new Subject<void>();
  private promptOpen = false;
  /** Completed in this app session - its write may still be in flight. */
  private completed = false;

  constructor(
    private router: Router,
    private coachMarkService: CoachMarkService,
    private paymentsService: PaymentsService,
    private onboardingStepService: OnboardingStepService,
    private entry: ScoreContextualEntryService,
    private ss01SuperStars: SS01SuperStarsService,
    private interactionTracking: InteractionTrackingService
  ) {
    // Logout emits false - forget this account's state.
    this.paymentsService.subscriptionActive$.pipe(filter((active) => !active)).subscribe(() => {
      this.completed = false;
      this.onDialogClosed();
    });
  }

  /** Call when the Super Stars modal opens. */
  onDialogOpened(): void {
    this.onDialogClosed();
    this.entry
      .enabled$()
      .pipe(
        switchMap(() => this.onboardingStepService.getSteps()),
        takeUntil(this.close$)
      )
      .subscribe((steps) => {
        const step = this.eligibleStep(steps);
        if (step !== false) {
          this.open(step);
        }
      });
  }

  /** Call when a Super Star's + is used - completes SS02 while its prompt shows. */
  onStarSelected(): void {
    if (!this.promptOpen) return;
    this.promptOpen = false;
    this.coachMarkService.dismiss();
    this.track("onboarding_lesson_action", "action: super_star_added");
    this.complete();
  }

  /** Call when the Super Stars modal closes. Records nothing. */
  onDialogClosed(): void {
    this.close$.next();
    if (this.promptOpen) {
      this.promptOpen = false;
      this.coachMarkService.dismiss();
    }
  }

  /** SS02's row (null if none yet) when eligible, or false. */
  private eligibleStep(steps: OnboardingStep[] | undefined): OnboardingStep | null | false {
    // Only a successful read qualifies - "couldn't check" holds back rather than guessing.
    // SS01B (workbook v2.47) has its turn first, whenever it was eligible to show.
    if (this.completed || steps === undefined || this.ss01SuperStars.hasPendingViewAll) {
      return false;
    }
    const ss01 = steps.find((s) => s.stepKey === SS01_LESSON_ID);
    const ss01Resolved = !!(ss01?.completedAt || ss01?.dismissed) || this.ss01SuperStars.hasResolved;
    const step = steps.find((s) => s.stepKey === SS02_LESSON_ID) ?? null;
    return ss01Resolved && !step?.completedAt && !step?.dismissed ? step : false;
  }

  private open(step: OnboardingStep | null): void {
    this.promptOpen = true;
    this.entry.markPresented(SS02_LESSON_ID);
    const now = new Date();
    this.onboardingStepService.upsertStep({
      stepKey: SS02_LESSON_ID,
      lastViewedAt: now,
      ...(step?.startedAt ? {} : { startedAt: now }),
    });

    // The engine tracks started / presented and the close for lessonId. The target lives inside the
    // open dialog, so the prompt must not wait for the dialog to close.
    this.coachMarkService.show({
      target: SS02_ANCHOR,
      heading: SS02_COPY.heading,
      message: SS02_COPY.body,
      lessonId: SS02_LESSON_ID,
      showOverDialogs: true,
      // Always under the first Star's + - never flipped above it, where it would cover the modal's
      // header copy; the engine scrolls the list to make room instead.
      lockedSide: "below",
      // Override: the standard task dismissal completes SS02 rather than deferring it.
      onDismiss: () => {
        this.promptOpen = false;
        this.complete();
      },
    });
  }

  private complete(): void {
    this.completed = true;
    this.onboardingStepService.upsertStep({ stepKey: SS02_LESSON_ID, completedAt: new Date() });
    this.track("onboarding_lesson_state_changed", `completed; milestone: ${SCORE_SUPER_STARS_QUICK_ADD_INTRODUCED}`);
  }

  /** Same shape as CoachMarkService's FLIP-322 lesson events. */
  private track(eventName: string, detail?: string): void {
    this.interactionTracking.track({
      screen: this.router.url,
      eventName,
      interactionType: InteractionType.Selection,
      entityType: "OnboardingLesson",
      detail: detail ? `lesson: ${SS02_LESSON_ID}; ${detail}` : `lesson: ${SS02_LESSON_ID}`,
    });
  }
}
